import { Component, computed, effect, forwardRef, input, signal } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import {
  CategorizedStatus,
  StatusPhase,
  StatusSelectOption,
  statusCode,
  statusLabel,
  statusPhases,
  statusTitle,
} from "../status-select/status-grouping";

/** Status mit den Flags, deren Wirkung die Auswahl vorab erklärt. */
export interface PickableStatus extends CategorizedStatus {
  id?: number;
  title?: string;
  color?: string;
  closed?: boolean;
  canceled?: boolean;
  report_closed?: boolean;
  visible_app?: boolean;
  unplanned?: boolean;
  mail_template_id?: number | null;
  email?: string | null;
}

/** Eine Auswirkung des Statuswechsels, wie sie im Panel steht. */
export interface StatusEffect {
  text: string;
  tone: "neutral" | "done" | "warn" | "mail";
  /** Vollständiger Text für den Tooltip, wenn `text` gekürzt ist */
  detail?: string;
}

/** "a@x.de; b@y.de" -> "a@x.de und 1 weitere" (Liste bleibt im Tooltip). */
function recipientsText(raw: string): StatusEffect {
  const recipients = raw
    .split(/[;,\s]+/)
    .map((r) => r.trim())
    .filter(Boolean);
  const [first, ...rest] = recipients;
  const text = rest.length ? `Benachrichtigt ${first} und ${rest.length} weitere` : `Benachrichtigt ${first}`;
  return { text, tone: "mail", detail: recipients.join(", ") };
}

/** Was ein Wechsel auf diesen Status auslöst – in der Sprache der Disposition. */
export function statusEffects(status: PickableStatus | null | undefined): StatusEffect[] {
  if (!status) return [];
  const effects: StatusEffect[] = [];
  if (status.canceled) {
    effects.push({ text: "Ticket wird storniert", tone: "warn" });
  } else if (status.closed) {
    effects.push({ text: "Ticket wird abgeschlossen", tone: "done" });
  } else {
    effects.push({ text: "Ticket bleibt offen", tone: "neutral" });
  }
  if (status.mail_template_id) {
    effects.push({ text: "Löst die Status-Mail an die Projektkontakte aus", tone: "mail" });
  }
  if (status.email?.trim()) {
    effects.push(recipientsText(status.email));
  }
  effects.push(
    status.visible_app
      ? { text: "Für Techniker in der App sichtbar", tone: "neutral" }
      : { text: "In der Techniker-App ausgeblendet", tone: "neutral" },
  );
  if (status.report_closed) {
    effects.push({ text: "Einsatzbericht für Techniker gesperrt", tone: "neutral" });
  }
  if (status.unplanned) {
    effects.push({ text: "Zählt als ungeplant in der Disposition", tone: "neutral" });
  }
  return effects;
}

/**
 * Zweistufige Statusauswahl der Ticketseite (hawk#606).
 *
 * Oben der Ticket-Ablauf als Leiste der Hauptkategorien (2 → 8), darunter die
 * Status der gewählten Phase und ein Panel, das vor dem Speichern zeigt, was der
 * Wechsel auslöst. Wert = Status-ID; nutzbar mit `formControlName`.
 */
@Component({
  selector: "hawk-status-picker",
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StatusPickerComponent),
      multi: true,
    },
  ],
  host: { class: "block" },
  styles: `
    :host {
      --sp-ink: var(--color-primary);
      --sp-line: var(--color-base-300);
    }
    .sp-track {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(6.5rem, 1fr);
      overflow-x: auto;
      scrollbar-width: thin;
    }
    .sp-track {
      align-items: start;
      padding-top: 0.25rem;
    }
    .sp-step {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 0.375rem;
      height: auto;
      min-height: 5.25rem;
      margin: 0;
      padding: 0.25rem 0.75rem 0.5rem 0.25rem;
      border: 0;
      background: transparent;
      text-align: left;
      cursor: pointer;
      border-radius: var(--radius-field);
      outline-offset: 2px;
    }
    .sp-step > * {
      flex-shrink: 0;
    }
    .sp-step:focus-visible {
      outline: 2px solid var(--sp-ink);
    }
    /* Verbindungslinie zum nächsten Schritt */
    .sp-step::after {
      content: "";
      position: absolute;
      top: 1.125rem;
      left: 2.25rem;
      z-index: 0;
      right: -0.25rem;
      height: 2px;
      background: var(--sp-line);
    }
    .sp-step:last-child::after,
    .sp-step.sp-legacy::after {
      display: none;
    }
    .sp-node {
      position: relative;
      z-index: 1;
      display: grid;
      place-items: center;
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 999px;
      border: 2px solid var(--sp-phase);
      background: var(--color-base-100);
      color: var(--sp-ink);
      font-weight: 700;
      font-size: 0.8125rem;
      font-variant-numeric: tabular-nums;
      transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
    }
    .sp-step[aria-selected="true"] .sp-node {
      background: var(--sp-phase);
      color: #fff;
      transform: scale(1.12);
    }
    .sp-step:hover .sp-node {
      transform: scale(1.06);
    }
    .sp-step[aria-selected="true"]:hover .sp-node {
      transform: scale(1.12);
    }
    .sp-step-title {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      max-width: 8.5rem;
      font-size: 0.75rem;
      line-height: 1.25;
      color: color-mix(in oklab, var(--color-base-content) 70%, transparent);
    }
    .sp-step[aria-selected="true"] .sp-step-title {
      color: var(--color-base-content);
      font-weight: 600;
    }
    /* Markierung "hier steht das Ticket" unter der Bezeichnung */
    .sp-here {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--sp-ink);
    }
    .sp-here::before {
      content: "";
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 999px;
      background: var(--sp-phase);
    }
    .sp-row {
      display: grid;
      grid-template-columns: 3rem 1fr auto;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border-left: 3px solid transparent;
      border-radius: var(--radius-field);
      cursor: pointer;
    }
    .sp-row:hover {
      background: var(--color-base-200);
    }
    .sp-row:has(input:checked) {
      border-left-color: var(--sp-ink);
      background: color-mix(in oklab, var(--sp-ink) 7%, var(--color-base-100));
    }
    .sp-row:has(input:focus-visible) {
      outline: 2px solid var(--sp-ink);
      outline-offset: 1px;
    }
    .sp-row[aria-disabled="true"] {
      cursor: not-allowed;
      opacity: 0.5;
    }
    .sp-code {
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      color: var(--sp-ink);
    }
    .sp-list {
      animation: sp-in 180ms ease-out;
    }
    @keyframes sp-in {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .sp-node,
      .sp-list {
        transition: none;
        animation: none;
      }
    }
  `,
  template: `
    <div class="flex flex-col gap-4">
      <!-- Suche über alle Phasen -->
      <label class="bm-input w-full">
        <span class="fa fa-search text-base-content/50" aria-hidden="true"></span>
        <input
          type="search"
          placeholder="Status suchen, z. B. „Freigabe“ oder „7.4“"
          [value]="search()"
          (input)="search.set($any($event.target).value)"
          aria-label="Status suchen"
        />
      </label>

      @if (!searching()) {
        <!-- Ticket-Ablauf: Hauptkategorien als Schritte -->
        <div class="sp-track" role="tablist" aria-label="Ablauf des Tickets">
          @for (phase of phases(); track phase.main) {
            <button
              type="button"
              role="tab"
              class="sp-step"
              [class.sp-legacy]="phase.main === null"
              [style.--sp-phase]="phase.color || 'var(--color-base-content)'"
              [attr.aria-selected]="phase.main === activeMain()"
              [title]="phase.title"
              (click)="activeMain.set(phase.main)"
            >
              <span class="sp-node">{{ phase.main ?? "–" }}</span>
              <span class="sp-step-title">{{ phase.title }}</span>
              @if (currentStatus() && phase.main === (currentStatus()?.main_category ?? null)) {
                <span class="sp-here">Ticket steht hier</span>
              }
            </button>
          }
        </div>
      }

      <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_16rem]">
        <!-- Status der Phase bzw. Suchtreffer -->
        <div
          class="sp-list flex flex-col gap-0.5 max-h-80 overflow-y-auto pr-1"
          role="radiogroup"
          [attr.aria-label]="searching() ? 'Suchtreffer' : activePhase()?.title"
        >
          @for (status of visibleStatuses(); track status.id) {
            <label class="sp-row" [attr.aria-disabled]="status.disabled || null">
              <input
                type="radio"
                class="sr-only"
                name="hawk-status-picker"
                [value]="status.id"
                [checked]="status.id === value()"
                [disabled]="status.disabled || disabled()"
                (change)="select(status.id)"
              />
              <span class="sp-code">{{ code(status) || "–" }}</span>
              <span class="min-w-0">
                <span class="block truncate">{{ title(status) }}</span>
                @if (status.disabled) {
                  <span class="block text-xs text-base-content/60">Wird über die Mettler-Rückmeldung gesetzt</span>
                }
              </span>
              @if (status.id === currentStatus()?.id) {
                <span class="bm-badge bm-badge-ghost bm-badge-sm">aktuell</span>
              } @else if (status.canceled) {
                <span class="bm-badge bm-badge-error bm-badge-outline bm-badge-sm">storniert</span>
              } @else if (status.closed) {
                <span class="bm-badge bm-badge-success bm-badge-outline bm-badge-sm">schließt</span>
              }
            </label>
          } @empty {
            <p class="p-3 text-sm text-base-content/70">
              Kein Status passt zu „{{ search() }}“. Suche nach einer Bezeichnung oder einer Nummer wie 7.4.
            </p>
          }
        </div>

        <!-- Was der Wechsel auslöst -->
        <aside class="rounded-box bg-base-200 p-4 text-sm" aria-live="polite">
          <h4 class="font-semibold mb-3">Was passiert beim Wechsel</h4>
          @if (selectedStatus(); as target) {
            <ul class="flex flex-col gap-2">
              @for (effect of effects(); track effect.text) {
                <li class="flex gap-2 items-start" [attr.title]="effect.detail || null">
                  <span [class]="effectIcon(effect)" aria-hidden="true"></span>
                  <span class="min-w-0 break-words">{{ effect.text }}</span>
                </li>
              }
            </ul>
          } @else {
            <p class="text-base-content/70">Wähle einen Status, um zu sehen, was er auslöst.</p>
          }
        </aside>
      </div>

      <!-- Übergang alt → neu -->
      @if (selectedStatus(); as target) {
        <p class="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-base-300 pt-3 text-sm">
          @if (target.id === currentStatus()?.id) {
            <span class="text-base-content/70">Noch keine Änderung – das Ticket steht auf {{ label(target) }}.</span>
          } @else {
            @if (currentStatus(); as current) {
              <span class="text-base-content/70">{{ label(current) }}</span>
              <span class="fa fa-long-arrow-right text-base-content/50" aria-hidden="true"></span>
            }
            <span class="font-semibold">{{ label(target) }}</span>
          }
        </p>
      }
    </div>
  `,
})
export class StatusPickerComponent implements ControlValueAccessor {
  /** Status des Projekts (Ticket-Metadaten `status_list`). */
  public statuses = input<readonly PickableStatus[] | null | undefined>([]);
  /** Aktueller Status des Tickets – wird in Leiste und Liste markiert. */
  public currentStatusId = input<number | null | undefined>(null);
  /** Optional: markiert einzelne Status als nicht wählbar (z. B. MT „abgelehnt“). */
  public isOptionDisabled = input<((status: PickableStatus) => boolean) | undefined>(undefined);

  protected readonly code = statusCode;
  protected readonly title = statusTitle;
  protected readonly label = statusLabel;

  protected effectIcon(effect: StatusEffect): string {
    const icons: Record<StatusEffect["tone"], string> = {
      done: "fa-check-circle text-green-600",
      warn: "fa-ban text-error",
      mail: "fa-envelope-o text-primary",
      neutral: "fa-circle-o text-base-content/40",
    };
    return `fa mt-0.5 w-4 text-center ${icons[effect.tone]}`;
  }

  protected value = signal<number | null>(null);
  protected disabled = signal(false);
  protected search = signal("");
  protected activeMain = signal<number | null | undefined>(undefined);

  protected phases = computed<StatusPhase<PickableStatus>[]>(() =>
    statusPhases(this.statuses(), this.isOptionDisabled()),
  );
  private allOptions = computed(() => this.phases().flatMap((phase) => phase.statuses));
  protected searching = computed(() => this.search().trim().length > 0);

  protected currentStatus = computed(
    () => this.allOptions().find((s) => s.id === this.currentStatusId()) ?? null,
  );
  protected selectedStatus = computed(() => this.allOptions().find((s) => s.id === this.value()) ?? null);
  protected effects = computed(() => statusEffects(this.selectedStatus()));

  protected activePhase = computed(
    () => this.phases().find((phase) => phase.main === this.activeMain()) ?? this.phases()[0] ?? null,
  );

  protected visibleStatuses = computed<StatusSelectOption<PickableStatus>[]>(() => {
    if (!this.searching()) return this.activePhase()?.statuses ?? [];
    const term = this.search().trim().toLowerCase();
    return this.allOptions().filter((s) => statusLabel(s).toLowerCase().includes(term));
  });

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Beim Öffnen die Phase des gewählten (sonst aktuellen) Status zeigen
    effect(() => {
      if (this.activeMain() !== undefined) return;
      const anchor = this.selectedStatus() ?? this.currentStatus();
      if (anchor || this.phases().length) {
        this.activeMain.set(anchor ? (anchor.main_category ?? null) : (this.phases()[0]?.main ?? null));
      }
    });
  }

  protected select(id: number | undefined): void {
    const value = id ?? null;
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
    // Bei Auswahl aus der Suche die Leiste auf die Phase des Treffers stellen
    const picked = this.selectedStatus();
    if (picked) this.activeMain.set(picked.main_category ?? null);
    this.search.set("");
  }

  public writeValue(value: number | string | null): void {
    const numeric = value === null || value === undefined || value === "" ? null : Number(value);
    this.value.set(Number.isNaN(numeric) ? null : numeric);
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
